import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { finalize, Observable, tap } from 'rxjs';
import { Patient, SsnType } from '../types/patient';

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  private readonly API_URL = environment.apiUrl + '/rest/patient';

  private http = inject(HttpClient);
  private toastr = inject(ToastrService);

  selectedPatient = signal<Patient | null>(null);
  newPatient = signal<boolean>(false);
  canModifyPatient = signal<boolean>(true);
  patientModificationStarted = signal<boolean>(false);

  loadingStates = signal<{
    searchBy2T: boolean;
    searchByID: boolean;
    save: boolean;
    update: boolean;
  }>({
    searchBy2T: false,
    searchByID: false,
    save: false,
    update: false
  });

  readonly ssnTypes: SsnType[] = [
    { id: 1, name: 'TAJ szám' },
    { id: 3, name: 'Útlevél szám (külföldi állampolgár)' },
    { id: 5, name: 'Menedékes, kérelmező, befogadó ig. száma' },
    { id: 8, name: 'Európai egészségbiztosítási kártya' },
    { id: 9, name: 'Menekült, menedékes és oltalmazott státusz kérelmezése előtt' },
    { id: 20, name: 'PID azonosító' },
    { id: 21, name: 'Személyazonosító igazolvány' },
    { id: 22, name: 'Tartózkodási engedély szám' }
  ];

  findPatientById(id: number): Observable<Patient> {
    this.loadingStates.set({ ...this.loadingStates(), searchByID: true });
    return this.http.get<Patient>(`${this.API_URL}/${id}`).pipe(
      tap(patient => {
        this.selectedPatient.set(patient);
        this.newPatient.set(false);
      }),
      finalize(() => {
        this.loadingStates.set({ ...this.loadingStates(), searchByID: false });
      })
    );
  }

  searchPatientBy2T(birthDate: string, ssnType: number, ssn: string): Observable<Patient[]> {
    this.loadingStates.set({ ...this.loadingStates(), searchBy2T: true });
    return this.http.get<Patient[]>(`${this.API_URL}/${birthDate}/${ssnType}/${ssn}`).pipe(
      tap({
        next: (patients) => {
          if (patients.length === 0) {
            // No results found
            this.selectedPatient.set(null);
            this.newPatient.set(true);
            this.canModifyPatient.set(true);
            this.toastr.info('Páciens nem található. Kérem töltse ki az adatokat.');
          } else if (patients.length === 1) {
            // Single result - auto-select
            this.selectedPatient.set(patients[0]);
            this.newPatient.set(false);
            this.canModifyPatient.set(true);
            this.toastr.success('Páciens megtalálva');
          }
          // For multiple results (patients.length > 1), caller will handle dialog
        },
        error: () => {
          this.selectedPatient.set(null);
          this.newPatient.set(true);
          this.canModifyPatient.set(true);
          this.toastr.info('Páciens nem található. Kérem töltse ki az adatokat.');
          this.loadingStates.set({ ...this.loadingStates(), searchBy2T: false });
        },
        complete: () => {
          this.loadingStates.set({ ...this.loadingStates(), searchBy2T: false });
        }
      })
    );
  }

  savePatient(patient: Partial<Patient>): Observable<Patient> {
    this.loadingStates.set({ ...this.loadingStates(), save: true });
    return this.http.post<Patient>(this.API_URL + '/save', patient).pipe(
      tap({
        next: (savedPatient) => {
          this.selectedPatient.set(savedPatient);
          this.newPatient.set(false);
          this.toastr.success('Páciens sikeresen mentve');
        },
        error: () => {
          this.toastr.error('Hiba történt a páciens mentése során');
          this.loadingStates.set({ ...this.loadingStates(), save: false });
        },
        complete: () => {
          this.loadingStates.set({ ...this.loadingStates(), save: false });
        }
      })
    );
  }

  updatePatient(id: number, patient: Partial<Patient>): Observable<Patient> {
    this.loadingStates.set({ ...this.loadingStates(), update: true });
    return this.http.put<Patient>(`${this.API_URL}/modify/${id}`, patient).pipe(
      tap({
        next: (updatedPatient) => {
          this.selectedPatient.set(updatedPatient);
          this.patientModificationStarted.set(false);
          this.toastr.success('Páciens sikeresen módosítva');
        },
        error: () => {
          this.toastr.error('Hiba történt a páciens módosítása során');
          this.loadingStates.set({ ...this.loadingStates(), update: false });
        },
        complete: () => {
          this.loadingStates.set({ ...this.loadingStates(), update: false });
        }
      })
    );
  }

  resetPatientState(): void {
    this.selectedPatient.set(null);
    this.newPatient.set(false);
    this.canModifyPatient.set(true);
    this.patientModificationStarted.set(false);
  }

  /**
   * Filter patient table by birth name search term
   */
  filterPatientTable(patients: Patient[], searchTerm: string | null): Patient[] {
    if (!searchTerm || searchTerm.trim() === '') {
      return patients;
    }

    const term = searchTerm.toLowerCase().trim();
    return patients.filter(patient =>
      patient.birthName?.toLowerCase().includes(term)
    );
  }
}
